'use client'

import { sendQueuedEvents } from 'analytics/events/generics/queue'
import { sendConsentMode } from 'analytics/events/gtag/sendConsentMode/sendConsentMode'
import { sendPageview } from 'analytics/events/gtag/sendPageview/sendPageview'
import { setGtag } from 'analytics/events/gtag/setGtag/setGtag'
import { useAnalyticsImpressionsEffects } from 'analytics/hooks/useAnalyticsImpressionsEffects/useAnalyticsImpressionsEffects'
import { isPageViewSent } from 'analytics/utils/isPageViewSent/isPageViewSent'
import { resetDataLayer } from 'analytics/utils/resetDataLayer/resetDataLayer'
import { startGTM } from 'analytics/utils/startGTM/startGTM'
import { useCookiesContext } from 'cookies/hooks/useCookiesContext/useCookiesContext'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useMasterDataIsReady } from 'master-data/hooks/useMasterDataIsReady/useMasterDataIsReady'
import { usePathname, useSearchParams } from 'next/navigation'
import {
	createContext,
	type ReactNode,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useSession } from 'session/src/hooks/useSession'

export type AnalyticsContextType = {
	canSendAnalytics: boolean
}

export const AnalyticsContext = createContext<AnalyticsContextType>({
	canSendAnalytics: false,
})

interface AnalyticsProviderProps {
	readonly children: ReactNode
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
	const masterData = useMasterData()

	const [isGTMLoaded, setIsGTMLoaded] = useState(false)
	const pathname = usePathname()
	const searchParams = useSearchParams()

	const { masterDataIsReady } = useMasterDataIsReady()

	const session = useSession()
	const { cookiesConsent, categories } = useCookiesContext()

	useAnalyticsImpressionsEffects()

	useEffect(() => {
		window.dataLayer ||= []
		window.dataLayer.length = 0
		setGtag()
	}, [])

	useEffect(() => {
		if (window.dataLayer.length && isPageViewSent()) {
			resetDataLayer({ setIsGTMLoaded, masterData, session })
		} else {
			startGTM()
		}
	}, [pathname, searchParams])

	useEffect(() => {
		if (session.isReady && cookiesConsent) {
			sendConsentMode({ categories, session })
		}
	}, [categories, session.isReady, cookiesConsent])

	useEffect(() => {
		if (session.isReady && cookiesConsent && masterDataIsReady) {
			const pageViewSent = isPageViewSent()
			if (!pageViewSent) {
				sendPageview(setIsGTMLoaded, masterData, session)
			}
			sendQueuedEvents()
		}
	}, [masterDataIsReady, session.isReady, cookiesConsent])

	const value = useMemo(
		() => ({
			canSendAnalytics: Boolean(
				session.isReady && cookiesConsent && isGTMLoaded && masterDataIsReady
			),
		}),
		[session.isReady && cookiesConsent && isGTMLoaded && masterDataIsReady]
	)

	return (
		<AnalyticsContext.Provider value={value}>
			{children}
		</AnalyticsContext.Provider>
	)
}
